import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import superagent from 'superagent';
import { toast, ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';

import Breadcrumbs from '@components/Common/Breadcrumb';
import useQuery from '@helpers/JIFI/useQuery';
import { backendUrl } from '@helpers/consts.js';

import { Card, CardContent as CardBody } from '@shadcn/components/ui/card';
import { Row } from '@shadcn/components/ui/row';
import { Col } from '@shadcn/components/ui/col';
import { Button } from '@shadcn/components/ui/button';
import { Input as InputShadcn } from '@shadcn/components/ui/input';
import CustomSelect from '@shadcn/components/ui/react-select';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/ui/dialog';

const extractErrorMessage = (error) => {
  if (!error) {
    return 'Unexpected error';
  }

  const responseMessage = error.response?.body?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim().length > 0) {
    return responseMessage;
  }

  if (Array.isArray(responseMessage) && responseMessage.length > 0) {
    return responseMessage[0];
  }

  return error.message || 'Unexpected error';
};

const initialListState = {
  items: [],
  total: 0,
  limit: 20,
  offset: 0,
  loading: false,
};

const normalizeAssignableNodes = (nodes) => {
  if (!Array.isArray(nodes)) {
    return [];
  }

  return nodes.map((node) => {
    const codeValue = node.code || node.value || '';
    const nameValue = node.name || node.label || codeValue;
    const typeSuffix = node.type ? ` (${node.type})` : '';
    
    return {
      label: `${nameValue}${codeValue ? ` - ${codeValue}` : ''}${typeSuffix}`,
      value: codeValue,
    };
  });
};

const AssignSkuPage = () => {
  const history = useHistory();
  const { locationID } = useParams();

  const [locationData] = useQuery({
    url: `/inventory/location/${locationID}`,
    runQuery: Boolean(locationID),
  });

  const rootLocationCode = useMemo(() => locationData?.code || '', [locationData]);

  const [nodeOptions, setNodeOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [nodeError, setNodeError] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);

  const [assignedList, setAssignedList] = useState(initialListState);
  const [assignedSearch, setAssignedSearch] = useState('');
  const [unassignedList, setUnassignedList] = useState(initialListState);
  const [unassignedSearch, setUnassignedSearch] = useState('');
  const [selectedSkuMap, setSelectedSkuMap] = useState({});
  const [quantityErrors, setQuantityErrors] = useState({});
  const [reallocateSku, setReallocateSku] = useState(null);
  const [reallocateQuantity, setReallocateQuantity] = useState('');
  const [reallocateError, setReallocateError] = useState('');
  const [reallocateLoading, setReallocateLoading] = useState(false);

  const sanitizeNumericInput = (value) => {
    if (typeof value !== 'string') {
      return '';
    }

    return value.replace(/[^0-9]/g, '');
  };

  const fetchNodeOptions = useCallback(async () => {
    if (!rootLocationCode) {
      return;
    }

    setOptionsLoading(true);
    setNodeError('');
    setNodeOptions([]);
    try {
      const response = await superagent
        .get(`${backendUrl}/hierarchy/${rootLocationCode}/assignable-nodes`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      const parsedBody = response.body || {};
      const nodesPayload = parsedBody.data && Array.isArray(parsedBody.data)
        ? parsedBody.data
        : parsedBody;

      const normalizedNodes = normalizeAssignableNodes(nodesPayload);
      setNodeOptions(normalizedNodes);
      setSelectedNode((prev) => {
        if (!prev) {
          return prev;
        }

        return normalizedNodes.some((option) => option.value === prev) ? prev : null;
      });
    } catch (error) {
      setNodeError(extractErrorMessage(error));
    } finally {
      setOptionsLoading(false);
    }
  }, [rootLocationCode]);

  useEffect(() => {
    if (!rootLocationCode) {
      return;
    }

    fetchNodeOptions();
  }, [rootLocationCode, fetchNodeOptions]);

  const fetchAssigned = useCallback(
    async ({ offset = 0, search = assignedSearch } = {}) => {
      if (!rootLocationCode || !selectedNode) {
        return;
      }

      setAssignedList((prev) => ({ ...prev, loading: true }));
      try {
        const response = await superagent
          .get(
            `${backendUrl}/v2/inventoryRecords/locations/${rootLocationCode}/hierarchy/${selectedNode}/assigned`
          )
          .query({ limit: assignedList.limit, offset, search })
          .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
          .set('accept', 'json');

        const body = response.body || {};
        setAssignedList({
          items: body.data || [],
          total: body.total || 0,
          limit: body.limit || assignedList.limit,
          offset: body.offset || offset,
          loading: false,
        });
      } catch (error) {
        setAssignedList((prev) => ({ ...prev, loading: false }));
        toast.error(extractErrorMessage(error));
      }
    },
    [assignedList.limit, assignedSearch, rootLocationCode, selectedNode]
  );

  const fetchUnassigned = useCallback(
    async ({ offset = 0, search = unassignedSearch } = {}) => {
      if (!rootLocationCode) {
        return;
      }

      setUnassignedList((prev) => ({ ...prev, loading: true }));
      try {
        const response = await superagent
          .get(`${backendUrl}/v2/inventoryRecords/locations/${rootLocationCode}/unassigned`)
          .query({ limit: unassignedList.limit, offset, search })
          .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
          .set('accept', 'json');

        const body = response.body || {};
        const items = body.data || [];
        setUnassignedList({
          items,
          total: body.total || 0,
          limit: body.limit || unassignedList.limit,
          offset: body.offset || offset,
          loading: false,
        });

        let nextErrors = {};
        setSelectedSkuMap((prev) => {
          const next = {};
          items.forEach((item) => {
            const prevEntry = prev[item.sku];
            if (!prevEntry) {
              return;
            }

            const remaining = item.remainingQuantity ?? prevEntry.remaining ?? 0;
            const quantity = prevEntry.quantity;

            next[item.sku] = {
              quantity,
              remaining,
            };

            if (quantity) {
              const numeric = Number(quantity);
              if (!Number.isFinite(numeric) || numeric <= 0) {
                nextErrors[item.sku] = 'Quantity must be greater than 0';
              } else if (numeric > remaining) {
                nextErrors[item.sku] = `Max ${remaining}`;
              }
            }
          });

          return next;
        });

        setQuantityErrors(nextErrors);
      } catch (error) {
        setUnassignedList((prev) => ({ ...prev, loading: false }));
        toast.error(extractErrorMessage(error));
      }
    },
    [rootLocationCode, unassignedList.limit, unassignedSearch]
  );

  useEffect(() => {
    if (!selectedNode) {
      setAssignedList((prev) => ({
        ...prev,
        items: [],
        total: 0,
        offset: 0,
        loading: false,
      }));
      setUnassignedList((prev) => ({
        ...prev,
        items: [],
        total: 0,
        offset: 0,
        loading: false,
      }));
      setSelectedSkuMap({});
      setQuantityErrors({});
      return;
    }

    fetchAssigned({ offset: 0 });
    fetchUnassigned({ offset: 0 });
    setSelectedSkuMap({});
    setQuantityErrors({});
  }, [selectedNode, fetchAssigned, fetchUnassigned]);

  const toggleSkuSelection = (item) => {
    const { sku, remainingQuantity } = item;

    setSelectedSkuMap((prev) => {
      if (prev[sku]) {
        const { [sku]: _removed, ...rest } = prev;
        setQuantityErrors((prevErrors) => {
          if (!prevErrors[sku]) {
            return prevErrors;
          }
          const { [sku]: _err, ...restErrors } = prevErrors;
          return restErrors;
        });
        return rest;
      }

      return {
        ...prev,
        [sku]: {
          quantity: '',
          remaining: remainingQuantity ?? 0,
        },
      };
    });
  };

  const setQuantityForSku = (sku, value, remaining) => {
    const sanitizedValue = sanitizeNumericInput(value);
    setSelectedSkuMap((prev) => {
      if (!prev[sku]) {
        return prev;
      }

      

      return {
        ...prev,
        [sku]: {
          quantity: sanitizedValue,
          remaining,
        },
      };
    });

    setQuantityErrors((prev) => {
      const next = { ...prev };
      if (!sanitizedValue) {
        delete next[sku];
        return next;
      }

      const numeric = Number(sanitizedValue);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        next[sku] = 'Quantity must be greater than 0';
      } else if (numeric > remaining) {
        next[sku] = `Max ${remaining}`;
      } else {
        delete next[sku];
      }

      return next;
    });
  };

  const handleAssignSku = async () => {
    if (!selectedNode) {
      toast.error('Please select a hierarchy node before assigning SKU');
      return;
    }

    const skus = Object.entries(selectedSkuMap);
    if (skus.length === 0) {
      toast.error('Please select at least one SKU');
      return;
    }

    const errors = {};
    const payloadSkus = [];

    skus.forEach(([sku, info]) => {
      const remaining = info.remaining ?? 0;
      const rawQuantity = (info.quantity ?? '').toString().trim();

      if (rawQuantity === '') {
        payloadSkus.push(sku);
        return;
      }

      const numeric = Number(rawQuantity);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        errors[sku] = 'Quantity must be greater than 0';
        return;
      }

      if (numeric > remaining) {
        errors[sku] = `Max ${remaining}`;
        return;
      }

      payloadSkus.push({ sku, quantity: numeric });
    });

    if (Object.keys(errors).length > 0) {
      setQuantityErrors(errors);
      toast.error('Please fix quantity errors before assigning');
      return;
    }

    try {
      let endpoint = `${backendUrl}/v2/inventoryRecords/assignToLocation`;
      let payload;

      if (payloadSkus.length === 1) {
        const skuEntry = payloadSkus[0];
        if (typeof skuEntry === 'object') {
          payload = { ...skuEntry, locationCode: selectedNode };
        } else {
          payload = { sku: skuEntry, locationCode: selectedNode };
        }
      } else {
        endpoint = `${backendUrl}/v2/inventoryRecords/bulkAssignToLocation`;
        payload = { skus: payloadSkus, locationCode: selectedNode };
      }

      const response = await superagent
        .post(endpoint)
        .send(payload)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      const result = response.body || {};

      if (payloadSkus.length > 1) {
        const data = result.data || {};
        const successCount = data.successCount ?? payloadSkus.length;
        const errorCount = data.errorCount ?? 0;

        toast.success(`Assigned ${successCount} SKU(s). Errors: ${errorCount}`);
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          console.warn('SKU assignment errors', data.errors);
        }
      } else {
        toast.success(result.message || 'SKU assigned successfully');
      }

      setSelectedSkuMap({});
      setQuantityErrors({});
      fetchAssigned({ offset: assignedList.offset });
      fetchUnassigned({ offset: unassignedList.offset });
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleOpenReallocate = (item) => {
    setReallocateSku(item);
    setReallocateQuantity('');
    setReallocateError('');
  };

  const handleCloseReallocate = () => {
    if (reallocateLoading) {
      return;
    }
    setReallocateSku(null);
    setReallocateQuantity('');
    setReallocateError('');
  };

  const handleReallocate = async () => {
    if (!reallocateSku || !selectedNode) {
      return;
    }

    const sanitized = sanitizeNumericInput(reallocateQuantity);
    if (!sanitized) {
      setReallocateError('Quantity must be greater than 0');
      return;
    }

    const numeric = Number(sanitized);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setReallocateError('Quantity must be greater than 0');
      return;
    }

    const assignedQuantity = reallocateSku.assignedQuantity ?? 0;
    if (numeric > assignedQuantity) {
      setReallocateError(`Max ${assignedQuantity}`);
      return;
    }

    setReallocateLoading(true);
    setReallocateError('');

    try {
      await superagent
        .patch(`${backendUrl}/v2/inventoryRecords/reallocate`)
        .send({
          sku: reallocateSku.sku,
          rootLocationCode,
          hierarchyCode: selectedNode,
          quantity: numeric,
        })
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      toast.success('Reallocated quantity successfully');
      handleCloseReallocate();
      fetchAssigned({ offset: assignedList.offset });
      fetchUnassigned({ offset: unassignedList.offset });
    } catch (error) {
      setReallocateError(extractErrorMessage(error));
    } finally {
      setReallocateLoading(false);
    }
  };

  const handleBack = () => {
    history.push(`/location/edit/${locationID}`);
  };

  const renderAssignedRows = () => {
    if (assignedList.loading) {
      return (
        <tr>
          <td colSpan="6" className="py-3 text-center text-sm text-muted-foreground">
            Loading assigned SKUs...
          </td>
        </tr>
      );
    }

    if (assignedList.items.length === 0) {
      return (
        <tr>
          <td colSpan="6" className="py-3 text-center text-sm text-muted-foreground">
            No SKU assigned to this node.
          </td>
        </tr>
      );
    }

    return assignedList.items.map((item) => (
      <tr key={item.sku} className="border-b last:border-b-0">
        <td className="py-2 px-2 text-sm font-medium">{item.sku}</td>
        <td className="py-2 px-2 text-sm text-right">{item.onHand ?? '-'}</td>
        <td className="py-2 px-2 text-sm text-right">{item.assignedQuantity ?? '-'}</td>
        <td className="py-2 px-2 text-sm text-right">{item.totalAssignedQuantity ?? '-'}</td>
        <td className="py-2 px-2 text-sm text-right">{item.remainingQuantity ?? '-'}</td>
        <td className="py-2 px-2 text-sm text-right">
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-2 text-xs"
            onClick={() => handleOpenReallocate(item)}
            disabled={!item.assignedQuantity}
          >
            Reallocate
          </Button>
        </td>
      </tr>
    ));
  };

  const renderUnassignedRows = () => {
    if (unassignedList.loading) {
      return (
        <tr>
          <td colSpan="5" className="py-3 text-center text-sm text-muted-foreground">
            Loading available SKUs...
          </td>
        </tr>
      );
    }

    if (unassignedList.items.length === 0) {
      return (
        <tr>
          <td colSpan="5" className="py-3 text-center text-sm text-muted-foreground">
            No available SKU.
          </td>
        </tr>
      );
    }

    return unassignedList.items.map((item) => {
      const isSelected = Boolean(selectedSkuMap[item.sku]);
      const remaining = item.remainingQuantity ?? 0;
      const quantityValue = selectedSkuMap[item.sku]?.quantity ?? '';
      const error = quantityErrors[item.sku];

      return (
        <tr key={item.sku} className="border-b last:border-b-0 align-top">
          <td className="py-2 px-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => toggleSkuSelection(item)}
            />
          </td>
          <td className="py-2 px-2 text-sm font-medium">{item.sku}</td>
          
          <td className="py-2 px-2 text-sm">{remaining}</td>
          <td className="py-2 px-2 text-sm">
            <InputShadcn
              type="text"
              inputMode="numeric"
              value={quantityValue}
              disabled={!isSelected}
              
              className="h-8 w-20"
              onChange={(event) =>
                setQuantityForSku(item.sku, event.target.value, remaining)
              }
            />
            {error && (
              <p className="mt-1 text-xs text-destructive">{error}</p>
            )}
          </td>
        </tr>
      );
    });
  };

  const assignedTotalPages = Math.ceil(assignedList.total / assignedList.limit) || 1;
  const assignedCurrentPage = Math.floor(assignedList.offset / assignedList.limit) + 1;

  const unassignedTotalPages = Math.ceil(unassignedList.total / unassignedList.limit) || 1;
  const unassignedCurrentPage = Math.floor(unassignedList.offset / unassignedList.limit) + 1;

  return (
    <div className="page-content">
      <div className="w-full px-3 mx-auto xl:max-w-[85%]">
        <Breadcrumbs
          title="Ecommerce"
          breadcrumbItem="Assign SKU to Location Hierarchy"
          addOn={
            <Button variant="outline" onClick={handleBack}>
              Back to Location
            </Button>
          }
        />

        <Card className="mt-2">
          <CardBody className="pt-6">
            <Row className="gap-6">
              <Col className="w-full">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Root location code</p>
                    <p className="mt-1 text-base font-semibold">{rootLocationCode || '-'}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium">Hierarchy node</p>
                    <CustomSelect
                      className="mt-2"
                      placeholder={optionsLoading ? 'Loading nodes...' : 'Select a hierarchy node'}
                      isLoading={optionsLoading}
                      options={nodeOptions}
                      value={nodeOptions.find((option) => option.value === selectedNode) || null}
                      onChange={(option) => setSelectedNode(option ? option.value : null)}
                    />
                    {nodeError && (
                      <p className="mt-2 text-xs text-destructive">{nodeError}</p>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Row className="mt-6 gap-6">
          <Col className="sm:w-6/12">
            <Card>
              <CardBody className="pt-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-base font-semibold">Assigned SKU</h4>
                  <div className="flex items-center gap-2">
                    <InputShadcn
                      value={assignedSearch}
                      placeholder="Search SKU"
                      onChange={(event) => setAssignedSearch(event.target.value)}
                    />
                    <Button
                      variant="outline"
                      onClick={() => fetchAssigned({ offset: 0, search: assignedSearch })}
                      disabled={!selectedNode}
                    >
                      Search
                    </Button>
                  </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full border border-border text-left text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="py-2 px-2">SKU</th>
                        <th className="py-2 px-2">On</th>
                        <th className="py-2 px-2">Node</th>
                        <th className="py-2 px-2">Total</th>
                        <th className="py-2 px-2">Left</th>
                        <th className="py-2 px-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>{renderAssignedRows()}</tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                  <span>
                    Page {assignedCurrentPage} of {assignedTotalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={assignedCurrentPage <= 1}
                      onClick={() =>
                        fetchAssigned({
                          offset: Math.max(assignedList.offset - assignedList.limit, 0),
                        })
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={assignedCurrentPage >= assignedTotalPages}
                      onClick={() =>
                        fetchAssigned({
                          offset: assignedList.offset + assignedList.limit,
                        })
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col className="sm:w-6/12">
            <Card>
              <CardBody className="pt-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-base font-semibold">Available SKU</h4>
                  <div className="flex items-center gap-2">
                    <InputShadcn
                      value={unassignedSearch}
                      placeholder="Search SKU"
                      onChange={(event) => setUnassignedSearch(event.target.value)}
                    />
                    <Button
                      variant="outline"
                      onClick={() => fetchUnassigned({ offset: 0, search: unassignedSearch })}
                    >
                      Search
                    </Button>
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Leave quantity empty to allocate the entire remaining amount for the SKU.
                </p>

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full border border-border text-left text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="py-2 px-3 w-12">Select</th>
                        <th className="py-2 px-2">SKU</th>
                        
                        <th className="py-2 px-2">Remaining</th>
                        <th className="py-2 px-2">Assign Qty</th>
                      </tr>
                    </thead>
                    <tbody>{renderUnassignedRows()}</tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                  <span>
                    Page {unassignedCurrentPage} of {unassignedTotalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={unassignedCurrentPage <= 1}
                      onClick={() =>
                        fetchUnassigned({
                          offset: Math.max(unassignedList.offset - unassignedList.limit, 0),
                        })
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={unassignedCurrentPage >= unassignedTotalPages}
                      onClick={() =>
                        fetchUnassigned({
                          offset: unassignedList.offset + unassignedList.limit,
                        })
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button
                    onClick={handleAssignSku}
                    disabled={!selectedNode || Object.keys(selectedSkuMap).length === 0}
                  >
                    Assign Selected SKU
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <ToastContainer />

      <Dialog open={Boolean(reallocateSku)} onOpenChange={handleCloseReallocate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Reallocate SKU {reallocateSku?.sku}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Assigned quantity: {reallocateSku?.assignedQuantity ?? '-'}
            </div>

            <div>
              <p className="text-sm font-medium">Quantity</p>
              <InputShadcn
                type="text"
                inputMode="numeric"
                value={reallocateQuantity}
                onChange={(event) => {
                  const sanitized = sanitizeNumericInput(event.target.value);
                  setReallocateQuantity(sanitized);
                  setReallocateError('');
                }}
                placeholder="Quantity to remove"
              />
              {reallocateError && (
                <p className="mt-2 text-xs text-destructive">{reallocateError}</p>
              )}
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseReallocate}
              disabled={reallocateLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleReallocate}
              disabled={reallocateLoading}
            >
              {reallocateLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignSkuPage;
